import { z } from 'zod';

import { type UiElementAccess, uiElementAccessSchema } from './ui-access';

export type UiScalar = string | number | boolean | null;
export interface WorkspaceViewScope {
  workspaceId: string;
  viewId: string;
}
export interface WorkspaceViewRevision {
  epoch: string;
  sequence: number;
}
export interface UiObjectRef {
  kind: string;
  id: string;
  docId?: string;
  attributes?: Readonly<Record<string, UiScalar>>;
}
/** A rectangle in the viewport's CSS pixels, rounded. */
export interface UiBox {
  x: number;
  y: number;
  width: number;
  height: number;
}
/** How many choices of a field one read lists, and how long each may be. */
export const UI_FIELD_MAX_OPTIONS = 32;
export const UI_FIELD_OPTION_MAX_LENGTH = 80;

/** The labelled places around an element, as one line. */
export const UI_ELEMENT_WITHIN_MAX_LENGTH = 512;

/**
 * An element of what the user sees. `text`, `selected`, `disabled` and `refs`
 * are carried only where they say something: without them an element shows no
 * text of its own, is not selected, not disabled and refers to nothing.
 */
export interface UiSemanticElement {
  key: string;
  role: string;
  label: string;
  text?: string;
  /**
   * What it is inside of, outermost first: given for an element the user
   * picked and for what that element contains, so a pick is read in its place.
   */
  within?: string;
  selected?: boolean;
  disabled?: boolean;
  refs?: readonly UiObjectRef[];
  /** Exception from operating freely; absent for everything else. */
  access?: UiElementAccess;
  /** The choices a field with a fixed list offers, as the user reads them. */
  options?: readonly string[];
  /** Where an object of a drawn surface (canvas, graph, page) is on screen. */
  box?: UiBox;
}
const SURFACE_KEY = 'surface:';
/**
 * The key an object of a drawn surface is addressed by: the surface that draws
 * it and its id there. The workspace, the view and its revision are bound by
 * what carries the key.
 */
export const surfaceKeyPrefix = (ownerId: string) =>
  `${SURFACE_KEY}${ownerId}:`;
export const surfaceObjectKey = (ownerId: string, objectId: string) =>
  surfaceKeyPrefix(ownerId) + objectId;
/** Whether a key addresses the object with this id on some drawn surface. */
export const isSurfaceObjectKey = (key: string, objectId: string) =>
  key.startsWith(SURFACE_KEY) && key.endsWith(`:${objectId}`);
/**
 * The id an object has on its surface, read from the key that addresses it; a
 * text that is no such key is taken as the id itself. An element of a canvas
 * has an id without a colon, so its id is what follows the last one.
 */
export const surfaceObjectId = (keyOrId: string) =>
  keyOrId.startsWith(SURFACE_KEY)
    ? keyOrId.slice(keyOrId.lastIndexOf(':') + 1)
    : keyOrId;
/**
 * The kinds of canvas object whose text the canvas opens for typing on a click
 * or a double click, as the role the view gives them. A new kind that does the
 * same is added here.
 */
export const CANVAS_TEXT_KINDS: ReadonlySet<string> = new Set([
  'note',
  'sticky-note',
  'edgeless-text',
  'shape',
  'text',
  'connector',
  'group',
  'frame',
]);
export interface UiInteraction {
  sequence: number;
  kind: 'click' | 'selection';
  origin: 'user' | 'ai';
  target: UiSemanticElement;
  neighbours: readonly UiSemanticElement[];
}
export type WorkspaceNavigationArgProperty =
  | { type: 'string'; maxLength: number; enum?: readonly string[] }
  | { type: 'number'; minimum: number; maximum: number; integer?: boolean }
  | { type: 'boolean' }
  | { type: 'null' }
  | { type: 'array'; items: 'string' | 'number' | 'boolean'; maxItems: number };
export interface WorkspaceNavigationArgsSchema {
  type: 'object';
  additionalProperties: false;
  properties: Readonly<Record<string, WorkspaceNavigationArgProperty>>;
  required: readonly string[];
}
export interface WorkspaceNavigationDescriptor {
  /** Semantic purpose of a genuine offered navigation, independent of its scoped ID. */
  capability?: 'workspace-search';
  searchText: readonly string[];
  actionId: string;
  ownerId: string;
  kind: 'navigate';
  label: string;
  argsSchema: WorkspaceNavigationArgsSchema;
  available: boolean;
}
export interface WorkspaceSurfaceContent {
  /** The native producer stopped at its bounded work/input quota. */
  truncated?: boolean;
  surfaceId: string;
  route: string;
  title: string;
  summary: string;
  refs: readonly UiObjectRef[];
  elements: readonly UiSemanticElement[];
  interactions: readonly UiInteraction[];
  actions: readonly WorkspaceNavigationDescriptor[];
}
export interface WorkspaceViewSnapshot
  extends WorkspaceViewScope, WorkspaceSurfaceContent {
  schemaVersion: 1;
  revision: WorkspaceViewRevision;
  truncated: boolean;
}
export type WorkspaceContextIssue =
  | 'unavailable'
  | 'stale'
  | 'blocked'
  | 'offline'
  | 'invalid'
  | 'oversized'
  | 'timeout';
export type WorkspaceViewReadResult =
  | { ok: true; snapshot: WorkspaceViewSnapshot }
  | { ok: false; reason: WorkspaceContextIssue; scope: WorkspaceViewScope };
export type WorkspaceViewParseResult =
  | { ok: true; snapshot: WorkspaceViewSnapshot }
  | { ok: false; reason: WorkspaceContextIssue };

export const WORKSPACE_VIEW_MAX_BYTES = 16_384;
export const WORKSPACE_VIEW_MAX_ESTIMATED_TOKENS = 4_000;
export const WORKSPACE_VIEW_ESTIMATED_BYTES_PER_TOKEN = 3;
/** Finite native reference work, including empty/duplicate selection entries. */
export const WORKSPACE_VIEW_MAX_REF_VISITS = 128;
/** Native commands and semantic metadata per automatic navigation projection. */
export const WORKSPACE_VIEW_MAX_NAVIGATION_VISITS = 128;
export const WORKSPACE_VIEW_MAX_INTERACTIONS = 8;
export const WORKSPACE_VIEW_MAX_NEIGHBOURS = 2;
export const WORKSPACE_VIEW_MAX_TITLE_CODE_POINTS = 120;
export const WORKSPACE_VIEW_MAX_ROUTE_CODE_POINTS = 512;

/** Reject impossible binding text before encoding or allocating a character array. */
export function isWithinWorkspaceViewCodePointLimit(
  value: string,
  max: number
): boolean {
  if (!Number.isSafeInteger(max) || max < 0) return false;
  if (value.length <= max) return true;
  if (value.length > 2 * max) return false;
  let count = 0;
  for (const _point of value) {
    if (++count > max) return false;
  }
  return true;
}

const nonEmpty = z.string().trim().min(1);
const positiveInteger = z
  .number()
  .int()
  .positive()
  .max(Number.MAX_SAFE_INTEGER);
const nonNegativeInteger = z
  .number()
  .int()
  .nonnegative()
  .max(Number.MAX_SAFE_INTEGER);
const scalar = z.union([
  z.string(),
  z.number().finite(),
  z.boolean(),
  z.null(),
]);
const objectRef = z
  .object({
    kind: nonEmpty,
    id: nonEmpty,
    docId: nonEmpty.optional(),
    attributes: z.record(z.string(), scalar).optional(),
  })
  .strict()
  .refine(ref => ref.kind !== 'doc' || !ref.docId || ref.id === ref.docId);
const pixel = z.number().int().min(-1_000_000).max(1_000_000);
export const uiBoxSchema: z.ZodType<UiBox> = z
  .object({
    x: pixel,
    y: pixel,
    width: pixel.nonnegative(),
    height: pixel.nonnegative(),
  })
  .strict();
const semanticElement = z
  .object({
    key: nonEmpty,
    role: nonEmpty,
    label: z.string(),
    text: z.string().optional(),
    within: z.string().min(1).max(UI_ELEMENT_WITHIN_MAX_LENGTH).optional(),
    selected: z.boolean().optional(),
    disabled: z.boolean().optional(),
    refs: z.array(objectRef).optional(),
    access: uiElementAccessSchema.optional(),
    options: z
      .array(z.string().max(UI_FIELD_OPTION_MAX_LENGTH))
      .max(UI_FIELD_MAX_OPTIONS)
      .optional(),
    box: uiBoxSchema.optional(),
  })
  .strict();
const argProperty = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('string'),
      maxLength: nonNegativeInteger,
      enum: z.array(z.string()).optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('number'),
      minimum: z.number().finite(),
      maximum: z.number().finite(),
      integer: z.boolean().optional(),
    })
    .strict(),
  z.object({ type: z.literal('boolean') }).strict(),
  z.object({ type: z.literal('null') }).strict(),
  z
    .object({
      type: z.literal('array'),
      items: z.enum(['string', 'number', 'boolean']),
      maxItems: nonNegativeInteger,
    })
    .strict(),
]);
export const workspaceNavigationArgsSchema = z
  .object({
    type: z.literal('object'),
    additionalProperties: z.literal(false),
    properties: z.record(z.string(), argProperty),
    required: z.array(nonEmpty),
  })
  .strict()
  .superRefine((schema, ctx) => {
    if (
      new Set(schema.required).size !== schema.required.length ||
      schema.required.some(
        key => !Object.prototype.hasOwnProperty.call(schema.properties, key)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid required argument keys',
      });
    }
    for (const [key, property] of Object.entries(schema.properties)) {
      if (
        !key.trim() ||
        ['__proto__', 'constructor', 'prototype'].includes(key)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid argument key',
        });
      }
      if (property.type === 'number' && property.minimum > property.maximum) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid argument range',
        });
      }
      if (
        property.type === 'string' &&
        property.enum?.some(
          value =>
            !isWithinWorkspaceViewCodePointLimit(value, property.maxLength)
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid argument enum',
        });
      }
    }
  });
/** Preview refs carry identity/provenance only, never navigation authority. */
function hasBoundPreviewReference(
  ref: UiObjectRef,
  scope: WorkspaceViewScope
): boolean {
  if (ref.kind !== 'preview-element') return true;
  const a = ref.attributes;
  if (!a) return false;
  const bounded = (value: UiScalar | undefined, max: number): value is string =>
    typeof value === 'string' &&
    value.length > 0 &&
    isWithinWorkspaceViewCodePointLimit(value, max) &&
    !/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/u.test(value);
  if (
    !['workspaceId', 'viewId', 'appBaseId', 'frameId', 'elementId'].every(key =>
      bounded(a[key], 256)
    ) ||
    a.workspaceId !== scope.workspaceId ||
    a.viewId !== scope.viewId ||
    a.elementId !== ref.id ||
    typeof a.artifactVersion !== 'number' ||
    !Number.isSafeInteger(a.artifactVersion) ||
    a.artifactVersion <= 0 ||
    !bounded(a.basePath, 2048) ||
    !bounded(a.routePath, 2048) ||
    !a.routePath.startsWith('/') ||
    /[\\?#]/u.test(a.routePath)
  )
    return false;
  const prefix = `/api/workspaces/${encodeURIComponent(String(a.workspaceId))}/app-bases/${encodeURIComponent(String(a.appBaseId))}/preview/${a.artifactVersion}/`;
  if (!a.basePath.startsWith(prefix)) return false;
  const slug = a.basePath.slice(prefix.length);
  return (
    !!slug &&
    !/[\/\\?#]/u.test(slug) &&
    !/%(?:2e|2f|5c)/iu.test(slug) &&
    slug !== '.' &&
    slug !== '..'
  );
}

const snapshotSchema = z
  .object({
    schemaVersion: z.literal(1),
    workspaceId: nonEmpty,
    viewId: nonEmpty,
    revision: z.object({ epoch: nonEmpty, sequence: positiveInteger }).strict(),
    surfaceId: nonEmpty,
    route: z
      .string()
      .refine(value =>
        isWithinWorkspaceViewCodePointLimit(
          value,
          WORKSPACE_VIEW_MAX_ROUTE_CODE_POINTS
        )
      ),
    title: z
      .string()
      .refine(value =>
        isWithinWorkspaceViewCodePointLimit(
          value,
          WORKSPACE_VIEW_MAX_TITLE_CODE_POINTS
        )
      ),
    summary: z.string(),
    refs: z.array(objectRef),
    elements: z.array(semanticElement),
    interactions: z
      .array(
        z
          .object({
            sequence: positiveInteger,
            kind: z.enum(['click', 'selection']),
            origin: z.enum(['user', 'ai']),
            target: semanticElement,
            neighbours: z
              .array(semanticElement)
              .max(WORKSPACE_VIEW_MAX_NEIGHBOURS),
          })
          .strict()
      )
      .max(WORKSPACE_VIEW_MAX_INTERACTIONS),
    actions: z.array(
      z
        .object({
          searchText: z.array(z.string()),
          capability: z.literal('workspace-search').optional(),
          actionId: nonEmpty,
          ownerId: nonEmpty,
          kind: z.literal('navigate'),
          label: z.string(),
          argsSchema: workspaceNavigationArgsSchema,
          available: z.boolean(),
        })
        .strict()
    ),
    truncated: z.boolean(),
  })
  .strict()
  .superRefine((snapshot, ctx) => {
    if (
      !getWorkspaceViewRefs(snapshot).every(ref =>
        hasBoundPreviewReference(ref, snapshot)
      )
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid preview reference binding',
      });
  });

/** Byte-based deterministic estimate, not a provider tokenizer's actual count. */
export function estimateWorkspaceViewSize(
  value: unknown
): { bytes: number; estimatedTokens: number } | null {
  try {
    const serialized = JSON.stringify(value);
    if (typeof serialized !== 'string') return null;
    const bytes = new TextEncoder().encode(serialized).length;
    return {
      bytes,
      estimatedTokens: Math.ceil(
        bytes / WORKSPACE_VIEW_ESTIMATED_BYTES_PER_TOKEN
      ),
    };
  } catch {
    return null;
  }
}

export function parseWorkspaceViewSnapshot(
  value: unknown,
  expectedWorkspaceId: string
): WorkspaceViewParseResult {
  const size = estimateWorkspaceViewSize(value);
  if (!size) return { ok: false, reason: 'invalid' };
  if (
    size.bytes > WORKSPACE_VIEW_MAX_BYTES ||
    size.estimatedTokens > WORKSPACE_VIEW_MAX_ESTIMATED_TOKENS
  ) {
    return { ok: false, reason: 'oversized' };
  }
  const parsed = snapshotSchema.safeParse(value);
  if (!parsed.success) return { ok: false, reason: 'invalid' };
  if (!expectedWorkspaceId || parsed.data.workspaceId !== expectedWorkspaceId)
    return { ok: false, reason: 'blocked' };
  return { ok: true, snapshot: parsed.data };
}

/** Everything a view refers to: its own references and those of every element it names. */
export function getWorkspaceViewRefs(
  snapshot: Pick<WorkspaceViewSnapshot, 'refs' | 'elements' | 'interactions'>
): UiObjectRef[] {
  return [
    ...snapshot.refs,
    ...snapshot.elements.flatMap(element => element.refs ?? []),
    ...snapshot.interactions.flatMap(interaction => [
      ...(interaction.target.refs ?? []),
      ...interaction.neighbours.flatMap(element => element.refs ?? []),
    ]),
  ];
}

/** Every semantic document reference passes through the existing document policy. */
export function getWorkspaceViewDocIds(
  snapshot: WorkspaceViewSnapshot
): string[] {
  return [
    ...new Set(
      getWorkspaceViewRefs(snapshot).flatMap(ref => [
        ...(ref.docId ? [ref.docId] : []),
        ...(ref.kind === 'doc' ? [ref.id] : []),
      ])
    ),
  ];
}
